import { useState } from "react";
import { ArrowUpRight } from "lucide-react";
import { trackEvent } from "@/lib/analytics";

const LIVE_DEMOS = [
  {
    id: "forecast",
    eyebrow: "Demand & Inventory Intelligence",
    name: "Forecast Planner",
    launchLabel: "Open demo",
    url: "/demo/forecast",
    image: "/demos/forecast.webp",
    alt: "Demand Forecast Planner showing inventory controls, demand forecast chart, and replenishment recommendation.",
  },
  {
    id: "accountbook",
    eyebrow: "AI-Enabled CRM & ERP",
    name: "Accountbook",
    launchLabel: "Open demo",
    url: "https://demo-accountbook.devbroz.com",
    image: "/demos/accountbook.webp",
    alt: "Accountbook dashboard showing customer, vendor, quote, and order management metrics.",
  },
  {
    id: "supportos",
    eyebrow: "AI Customer Operations",
    name: "SupportOS",
    launchLabel: "Open demo",
    url: "/demo/support",
    image: "/demos/supportos.webp",
    alt: "SupportOS agent workflow view showing connected customer operations agents.",
  },
];

const CASES = [
  {
    id: 1,
    cat: "ai",
    name: "AI Property Assistant",
    client: "UK property management firm",
    tag: "AI & ML",
    outcome: "80% queries automated",
    problem:
      "Tenant teams spent most working hours repeating answers about leases, maintenance, and availability, with uneven quality after hours.",
    solution:
      "We delivered a conversational assistant trained on their portfolio and policies, routed into their CRM so only edge cases escalate to humans.",
    stack: ["RAG pipeline", "LLM APIs", "Vector DB", "CRM webhook"],
    metrics: [
      { label: "Queries handled by assistant", value: "80%" },
      { label: "Avg response (live channel)", value: "< 3s" },
      { label: "FAQ manual hours / week", value: "~18 hrs saved" },
    ],
  },
  {
    id: 2,
    cat: "data",
    name: "D2C Live Dashboard",
    client: "D2C lifestyle brand",
    tag: "Data Analytics",
    outcome: "4+ hrs reporting → 0",
    problem:
      "Growth leads stitched Shopify, Meta Ads, and GA into a spreadsheet every Monday—four-plus hours late, error-prone data.",
    solution:
      "We pipelined all three APIs into one live view with refreshed KPIs (revenue, ROAS, CAC) plus weekly email digests—no spreadsheets.",
    stack: ["BigQuery", "dbt", "Shopify API", "Looker Studio"],
    metrics: [
      { label: "Weekly manual reporting", value: "0 hrs" },
      { label: "Data freshness", value: "Near real-time" },
      { label: "Sources unified", value: "3 → 1" },
    ],
  },
  {
    id: 3,
    cat: "software",
    name: "E-Commerce Rebuild",
    client: "Premium fashion brand",
    tag: "Custom Software",
    outcome: "+44% mobile conversion",
    problem:
      "A legacy theme was slow on mobile (~71% of traffic); checkout dragged through seven friction-heavy steps.",
    solution:
      "Mobile-first storefront: lean checkout (three steps), lazy-loaded imagery, cart and trust fixes where heatmaps showed drop-off.",
    stack: ["Shopify", "Liquid", "Performance tuning", "Analytics"],
    metrics: [
      { label: "Mobile conversion uplift", value: "+44%" },
      { label: "Mobile LCP-style load", value: "~1.8s" },
      { label: "Checkout steps", value: "7 → 3" },
    ],
  },
  {
    id: 4,
    cat: "ai",
    name: "Churn Prediction Model",
    client: "SaaS e-commerce retailer",
    tag: "AI & ML",
    outcome: "89% prediction accuracy",
    problem:
      "Retention was reactive: blanket win-back sends after customers had already disengaged, with weak ROI visibility.",
    solution:
      "Daily churn-propensity scoring on behaviour and transactional history feeds targeted saves before lapse—wired into ESP and CS queues.",
    stack: ["Python", "Snowflake", "XGBoost", "Airflow"],
    metrics: [
      { label: "Model precision (holdout)", value: "89%" },
      { label: "Retention campaign ROI", value: "~4.6x" },
      { label: "LTV (saved cohort)", value: "+31%" },
    ],
  },
  {
    id: 5,
    cat: "software",
    name: "B2B Website Redesign",
    client: "Management consulting firm",
    tag: "Custom Software",
    outcome: "~55% more enquiries",
    problem:
      "An ageing site buried proof and lacked a conversion path—referrals dominated while warm web visitors bounced.",
    solution:
      "Buyer-intent IA, outcome-led service pages, visible proof, CRM-backed forms with SLA automation on first response.",
    stack: ["Next.js", "Headless CMS", "HubSpot", "Vercel"],
    metrics: [
      { label: "Inbound enquiries / month", value: "18 → 28" },
      { label: "Enquiry → proposal rate", value: "34% → 51%" },
      { label: "Qualified lead mix", value: "~40% → ~68%" },
    ],
  },
];

const FILTERS = [
  { key: "all", label: "All" },
  { key: "ai", label: "AI & ML" },
  { key: "data", label: "Data & Analytics" },
  { key: "software", label: "Custom Software" },
];

function DemoAnchor({ demo, className, children, ariaLabel, source }) {
  return (
    <a
      href={demo.url}
      className={className}
      aria-label={ariaLabel}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => trackEvent("live_demo_launch", { demo_name: demo.id, source })}
    >
      {children}
    </a>
  );
}

export default function CaseStudies() {
  const [activeFilter, setActiveFilter] = useState("all");
  const [expandedId, setExpandedId] = useState(null);

  const filtered =
    activeFilter === "all"
      ? CASES
      : CASES.filter((c) => c.cat === activeFilter);

  const toggle = (id) => setExpandedId(expandedId === id ? null : id);

  return (
    <section
      id="work"
      data-testid="case-studies-section"
      className="cs-section"
    >
      <div className="cs-container">
      {/* Header */}
      <div className="cs-header">
        <div className="cs-eyebrow">
          <span className="cs-eyebrow-line" />
          Selected work
        </div>
        <h2 className="cs-title" data-testid="case-studies-heading">
          From idea
          <br />
          <span className="cs-title-gradient">to impact.</span>
        </h2>
      </div>

      <div className="live-demos" aria-labelledby="live-demos-heading">
        <div className="live-demos-heading">
          <p>Live demonstrations</p>
          <h3 id="live-demos-heading">Explore the products we build.</h3>
          <span>Launch a working demo and interact with the experience.</span>
        </div>
        <div className="live-demos-grid">
          {LIVE_DEMOS.map((demo) => (
            <article className={`live-demo live-demo--${demo.id}`} key={demo.id}>
              <DemoAnchor
                demo={demo}
                className="live-demo-image"
                ariaLabel={`Launch ${demo.name} live demo`}
                source="work_showcase_image"
              >
                <img src={demo.image} alt={demo.alt} loading="lazy" decoding="async" />
              </DemoAnchor>
              <div className="live-demo-content">
                <p className="live-demo-eyebrow">{demo.eyebrow}</p>
                <h4>{demo.name}</h4>
                <div className="live-demo-actions">
                  <DemoAnchor demo={demo} className="live-demo-launch" source="work_showcase_cta">
                    {demo.launchLabel} <ArrowUpRight size={16} />
                  </DemoAnchor>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div className="cs-filters">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            className={`cs-filter ${activeFilter === f.key ? "cs-filter--active" : ""}`}
            onClick={() => {
              setActiveFilter(f.key);
              setExpandedId(null);
            }}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Column headers */}
      <div className="cs-col-headers">
        <span>Project</span>
        <span>Category</span>
        <span>Key result</span>
        <span />
      </div>

      {/* Case list — full bleed inside container (no horizontal padding on this box) */}
      <div className="cs-list">
        {filtered.map((c, i) => {
          const isOpen = expandedId === c.id;
          return (
            <div key={c.id}>
              {i > 0 && <div className="cs-divider" />}
              <div
                role="button"
                tabIndex={0}
                className={`cs-row ${isOpen ? "cs-row--open" : ""}`}
                onClick={() => toggle(c.id)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    toggle(c.id);
                  }
                }}
              >
                {/* Always-visible row */}
                <div className="cs-row-main">
                  <div
                    className="cs-col-project"
                    data-outcome={c.outcome}
                  >
                    <span className="cs-project-name">{c.name}</span>
                    <span className="cs-project-client">{c.client}</span>
                  </div>
                  <div className="cs-col-tag">{c.tag}</div>
                  <div className="cs-col-outcome">{c.outcome}</div>
                  <div className="cs-col-action">
                    View
                    <span className={`cs-chevron ${isOpen ? "cs-chevron--open" : ""}`}>
                      ↓
                    </span>
                  </div>
                </div>

                {/* Expanded detail */}
                {isOpen && (
                  <div className="cs-detail">
                    <div className="cs-detail-left">
                      <p className="cs-problem">{c.problem}</p>
                      <p className="cs-solution">{c.solution}</p>
                      <div className="cs-stack">
                        {c.stack.map((t) => (
                          <span key={t} className="cs-tag">
                            {t}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="cs-detail-right">
                      <div className="cs-metrics">
                        {c.metrics.map((m) => (
                          <div key={m.label} className="cs-metric">
                            <span className="cs-metric-label">{m.label}</span>
                            <span className="cs-metric-value">{m.value}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      </div>

      <style>{`
        .cs-section {
          background: #080808;
          padding: 80px 0 100px;
          font-family: inherit;
        }

        .cs-container {
          max-width: 1280px;
          margin-left: auto;
          margin-right: auto;
          padding-left: 40px;
          padding-right: 40px;
        }

        .cs-header {
          margin-bottom: 36px;
        }

        .cs-eyebrow {
          display: flex;
          align-items: center;
          gap: 10px;
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: #e040a0;
          margin-bottom: 16px;
        }

        .cs-eyebrow-line {
          display: block;
          width: 24px;
          height: 1.5px;
          background: #e040a0;
        }

        .cs-title {
          font-size: clamp(32px, 5vw, 52px);
          font-weight: 800;
          line-height: 1.1;
          color: #fff;
          margin-bottom: 12px;
        }

        .cs-title-gradient {
          background: linear-gradient(90deg, #e040a0, #7c3aed);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .cs-subtitle {
          font-size: 15px;
          color: #555;
          max-width: 400px;
          line-height: 1.6;
        }

        .live-demos {
          border-top: 1px solid #1e1e1e;
          margin: 40px 0 64px;
          padding-top: 28px;
        }

        .live-demos-heading {
          display: flex;
          flex-direction: column;
          gap: 8px;
          margin-bottom: 22px;
        }

        .live-demos-heading p {
          color: #e040a0;
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 0.12em;
          text-transform: uppercase;
        }

        .live-demos-heading h3 {
          color: #f5f5f5;
          font-size: clamp(24px, 3vw, 30px);
          font-weight: 700;
          letter-spacing: -0.04em;
          line-height: 1;
        }

        .live-demos-heading span {
          color: #686868;
          font-size: 13px;
          line-height: 1.5;
          max-width: 440px;
        }

        .live-demos-grid {
          display: grid;
          gap: 16px;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          max-width: 1040px;
        }

        .live-demo {
          aspect-ratio: 1;
          background: #111;
          border: 1px solid #202020;
          border-radius: 20px;
          display: flex;
          flex-direction: column;
          justify-content: flex-end;
          min-width: 0;
          overflow: hidden;
          position: relative;
          transition: border-color 0.2s, transform 0.2s;
        }

        .live-demo:hover {
          border-color: #424242;
          transform: translateY(-3px);
        }

        .live-demo-image {
          background: #151515;
          display: block;
          height: 63%;
          overflow: hidden;
          inset: 0 0 auto;
          position: absolute;
        }

        .live-demo-image::after {
          background: linear-gradient(180deg, transparent 75%, rgba(0, 0, 0, 0.18));
          content: '';
          inset: 0;
          pointer-events: none;
          position: absolute;
        }

        .live-demo-image img {
          display: block;
          height: 100%;
          object-fit: cover;
          object-position: top center;
          transition: transform 0.4s ease;
          width: 100%;
        }

        .live-demo:hover .live-demo-image img {
          transform: scale(1.025);
        }

        .live-demo-image:focus-visible {
          outline: 2px solid #e040a0;
          outline-offset: -2px;
        }

        .live-demo-content {
          background: #111;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          margin-top: auto;
          min-height: 37%;
          padding: 15px 18px 18px;
          position: relative;
          z-index: 1;
        }

        .live-demo-eyebrow {
          color: #b56892;
          font-size: 9px;
          font-weight: 700;
          letter-spacing: 0.1em;
          text-transform: uppercase;
        }

        .live-demo-content h4 {
          color: #f7f7f7;
          font-size: 21px;
          font-weight: 700;
          letter-spacing: -0.035em;
          line-height: 1.15;
          margin: 5px 0 12px;
        }

        .live-demo-actions {
          align-items: center;
          display: flex;
          gap: 12px;
        }

        .live-demo-launch {
          align-items: center;
          color: #e8b9d7;
          display: inline-flex;
          font-size: 12px;
          font-weight: 600;
          gap: 6px;
          padding: 0;
          text-decoration: none;
          transition: filter 0.2s;
        }

        .live-demo-launch:hover {
          color: #fff;
        }

        /* Filters */
        .cs-filters {
          display: flex;
          gap: 8px;
          margin-bottom: 36px;
          flex-wrap: wrap;
        }

        .cs-filter {
          padding: 7px 18px;
          border-radius: 99px;
          font-size: 12px;
          font-weight: 500;
          cursor: pointer;
          border: 0.5px solid #222;
          background: #111;
          color: #777;
          transition: border-color 0.15s, color 0.15s, background 0.15s;
          font-family: inherit;
        }

        .cs-filter:hover {
          border-color: #444;
          color: #ccc;
        }

        .cs-filter--active {
          background: linear-gradient(90deg, #e040a0, #7c3aed);
          border-color: transparent;
          color: #fff;
        }

        /* Column headers */
        .cs-col-headers {
          display: grid;
          grid-template-columns: 2fr 1fr 1fr 100px;
          padding-left: 28px;
          padding-right: 28px;
          margin-bottom: 6px;
          font-size: 10px;
          font-weight: 600;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: #2e2e2e;
        }

        /* List */
        .cs-list {
          background: #111;
          border-radius: 16px;
          overflow: hidden;
          border: 0.5px solid #1a1a1a;
          width: 100%;
          box-sizing: border-box;
        }

        .cs-divider {
          height: 0.5px;
          background: #1a1a1a;
        }

        /* Row */
        .cs-row {
          background: #0d0d0d;
          cursor: pointer;
          position: relative;
          overflow: hidden;
          transition: background 0.15s;
          padding: 0 28px;
        }

        .cs-row:focus-visible {
          outline: 2px solid #e040a0;
          outline-offset: -2px;
          z-index: 1;
        }

        .cs-row::before {
          content: '';
          position: absolute;
          left: 0;
          top: 0;
          bottom: 0;
          width: 0;
          background: linear-gradient(90deg, rgba(224, 64, 160, 0.1), transparent);
          transition: width 0.25s;
          pointer-events: none;
        }

        .cs-row:hover,
        .cs-row--open {
          background: #111;
        }

        .cs-row:hover::before,
        .cs-row--open::before {
          width: 100%;
        }

        .cs-row-main {
          display: grid;
          grid-template-columns: minmax(0, 2fr) minmax(0, 1fr) minmax(0, 1fr) 100px;
          align-items: center;
          height: 72px;
          column-gap: 12px;
        }

        .cs-col-project {
          display: flex;
          flex-direction: column;
          gap: 3px;
        }

        .cs-project-name {
          font-size: 14px;
          font-weight: 600;
          color: #fff;
        }

        .cs-project-client {
          font-size: 12px;
          color: #444;
        }

        .cs-col-tag {
          font-size: 12px;
          color: #555;
        }

        .cs-col-outcome {
          font-size: 13px;
          font-weight: 700;
          background: linear-gradient(90deg, #e040a0, #7c3aed);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .cs-col-action {
          display: flex;
          justify-content: flex-end;
          align-items: center;
          gap: 6px;
          font-size: 12px;
          color: #444;
          user-select: none;
        }

        .cs-chevron {
          display: inline-block;
          transition: transform 0.2s;
          font-size: 14px;
          color: #555;
        }

        .cs-chevron--open {
          transform: rotate(180deg);
        }

        /* Expanded detail */
        .cs-detail {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 32px;
          border-top: 0.5px solid #1f1f1f;
          padding: 24px 0 28px;
        }

        .cs-problem {
          font-size: 13px;
          color: #666;
          line-height: 1.75;
          margin-bottom: 14px;
        }

        .cs-solution {
          font-size: 13px;
          color: #999;
          line-height: 1.75;
        }

        .cs-stack {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
          margin-top: 18px;
        }

        .cs-tag {
          font-size: 11px;
          padding: 4px 10px;
          border-radius: 4px;
          background: #161616;
          border: 0.5px solid #252525;
          color: #555;
        }

        .cs-metrics {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .cs-metric {
          background: #161616;
          border: 0.5px solid #202020;
          border-radius: 10px;
          padding: 14px 18px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          min-width: 0;
        }

        .cs-metric-label {
          font-size: 12px;
          color: #444;
          min-width: 0;
        }

        .cs-metric-value {
          flex-shrink: 0;
          text-align: right;
          font-size: 20px;
          font-weight: 800;
          background: linear-gradient(90deg, #e040a0, #7c3aed);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        /* Responsive */
        @media (max-width: 768px) {
          .cs-section {
            padding: 60px 0 80px;
          }

          .cs-container {
            padding-left: 20px;
            padding-right: 20px;
          }

          .live-demos {
            margin: 36px 0 52px;
          }

          .live-demos-heading {
            display: block;
          }

          .live-demos-heading h3 {
            margin: 8px 0;
          }

          .live-demos-grid {
            grid-template-columns: 1fr;
          }

          .live-demo-content {
            padding: 14px 16px 16px;
          }

          .live-demo-content h4 {
            font-size: 21px;
          }

          .cs-col-headers {
            display: none;
          }

          .cs-row-main {
            grid-template-columns: 1fr auto;
            height: auto;
            padding: 18px 0;
            gap: 12px;
          }

          .cs-col-tag,
          .cs-col-outcome {
            display: none;
          }

          .cs-col-project {
            gap: 4px;
          }

          .cs-project-client {
            color: #555;
          }

          /* Show outcome under name on mobile */
          .cs-col-project::after {
            content: attr(data-outcome);
            font-size: 12px;
            font-weight: 700;
            background: linear-gradient(90deg, #e040a0, #7c3aed);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
            margin-top: 2px;
          }

          .cs-detail {
            grid-template-columns: 1fr;
            gap: 20px;
          }

          .cs-row {
            padding: 0 20px;
          }

          .cs-list {
            border-radius: 12px;
          }
        }
      `}</style>
    </section>
  );
}
